import { useEffect, useState } from "react";
import { CountdownFormat } from "@cede/utils";
import { CAMPAIGN_STATUS, CampaignSummaryWithStats, CampaignWithStats } from "@/components/features/campaigns/types";
import { computeTimeLeft } from "@/hooks/core/useCountdown";

const EPOCH_COUNT_UPDATE_INTERVAL = 1000;

export const useNextEpochCountdown = (
  campaignMetadata: CampaignWithStats | CampaignSummaryWithStats | null | undefined,
) => {
  const currentEpoch = campaignMetadata?.currentEpoch;
  const nextEpoch = campaignMetadata?.nextEpoch;
  const campaignDay = (currentEpoch?.activeEpochId ?? 0) + 1;

  // Calculate countdowns during render based on current data
  const initialNextEpochCountdown = nextEpoch
    ? computeTimeLeft(nextEpoch.start || new Date() || new Date())
    : {
        days: "0",
        hours: "0",
        minutes: "0",
        seconds: "0",
      };

  const initialCurrentEpochEndCountdown = currentEpoch
    ? computeTimeLeft(currentEpoch.end || new Date())
    : {
        days: "0",
        hours: "0",
        minutes: "0",
        seconds: "0",
      };

  const [nextEpochCountdown, setNextEpochCountdown] = useState<CountdownFormat>(initialNextEpochCountdown);
  const [currentEpochEndCountdown, setCurrentEpochEndCountdown] = useState<CountdownFormat>(
    initialCurrentEpochEndCountdown,
  );

  const computeCountdown = () => {
    if (currentEpoch) {
      setCurrentEpochEndCountdown(computeTimeLeft(currentEpoch.end || new Date()));
    }
    if (nextEpoch) {
      setNextEpochCountdown(computeTimeLeft(nextEpoch.start || new Date()));
    }
  };

  // Only use useEffect for the interval updates
  useEffect(() => {
    if (campaignMetadata?.status === CAMPAIGN_STATUS.PAST) return;

    const interval = setInterval(computeCountdown, EPOCH_COUNT_UPDATE_INTERVAL);
    return () => clearInterval(interval);
  }, [campaignMetadata, currentEpoch, nextEpoch]);

  return {
    nextEpochCountdown,
    campaignDay,
    currentEpoch,
    nextEpoch,
    currentEpochEndCountdown,
  };
};
